/**
 * pages/AccessGroupEditor.tsx — the detail surface for one group: what it confers, and who is in it.
 *
 * `06-modules.md` §2.2 requires exactly these two editors on one surface, and requires them together: an
 * app that can grant rights it cannot review has an administration screen in name only. The two panes here
 * are that pair, and the reason they share a dialog rather than living on two screens is that the questions
 * are asked together — *this group reaches Tropical Sun; who does that actually let in?*
 *
 * **Every write is answered, including the writes that succeed and do nothing.** The server returns
 * `{changed, warnings}` on every facet write, and three of those warnings describe a grant that is legal,
 * recorded, and inert: a role for a business unit the group cannot reach, an `act` clamped down to `view`,
 * a membership neutered by the person's confinement. The administrator who made the grant is the only one
 * who can still fix it cheaply, so the warning is shown to them at the moment they make it — not left for
 * whoever next reads the estate table. `changed: 0` is likewise reported rather than celebrated: it means
 * the row was already there.
 *
 * **Nothing here subtracts.** §3.1 scope access unions across a subject's groups and has no deny form, so
 * there is deliberately no "deny this business unit" control — the only lever that narrows what a group
 * confers is removing a row from it, or removing the person from the group. The copy says so where an
 * administrator would otherwise go looking.
 *
 * The rows are the database's own shapes, unmapped — `server/routes/admin.ts` returns `listScopeAccess`
 * and friends verbatim — so the snake_case field names are deliberate.
 */
import { useCallback, useMemo, useState } from "react";
import {
  ApiError,
  type AdminSection,
  type AdminUser,
  type ScopeRole,
} from "../lib/api";
import { formatDateTime, humanise, resourceKindLabel, roleLabel } from "../lib/format";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Modal,
  Select,
  Table,
  TextField,
  useToast,
  type Column,
} from "../components/ui";
import type {
  AccessWritePorts,
  BusinessUnitOption,
  GroupFacets,
  PolicyWarning,
  PolicyWriteResult,
} from "./accessTypes";

/** The four sections a group grant can name. `jobs` is deliberately absent — see the note below. */
const GRANTABLE_SECTIONS: readonly AdminSection[] = ["users", "access", "business_units", "audit"];

/**
 * The three grants, described by what they now DO — which since 7 August 2026 includes making a section
 * visible at all.
 *
 * Until that date a role gated only the writes: anyone who could reach the business unit could read the
 * Automations list and every run's history, and Goals borrowed `job_author` for its writes. So all three
 * sentences moved, not just the new one. Describing `goal_author` as "post check-ins" while leaving
 * `job_author` as "create and edit scheduled updates" would leave an administrator believing this picker
 * is about writing, on the one screen where they decide whether somebody can see a section at all.
 *
 * `bu_admin` says "both of the above" rather than "the above" for the same reason: it now admits BOTH
 * modules — the owner's ruling of 7 August 2026, so that an administrator holding only it does not sign in
 * to an empty shell. It does NOT confer the authoring verbs; `requireScopeRole` is literal and there is no
 * hierarchy inside the resolver, so a `bu_admin` can open Goals and still not post a check-in.
 */
const SCOPE_ROLE_OPTIONS: readonly { readonly value: ScopeRole; readonly label: string }[] = [
  {
    value: "job_author",
    label: `${roleLabel("job_author")} — see the Automations section at all, and create and edit the scheduled updates in it`,
  },
  {
    value: "goal_author",
    label: `${roleLabel("goal_author")} — see the Goals section at all, and register objectives and post their check-ins to Atlassian`,
  },
  {
    value: "bu_admin",
    label: `${roleLabel("bu_admin")} — see both of the above sections, plus reassigning an update's owner`,
  },
];

/**
 * `hide` narrows to the two kinds the server accepts; anything else is a 404 rather than a validation error.
 *
 * ⚠️ A HAND-TRANSCRIPTION of `server/permissions/types.ts`'s `RESOURCE_KINDS`, because the client shares
 * no module with the server. There is deliberately no write axis: a document update pushes only to the
 * repository its job selected, and selecting it already required the `repo` grant below.
 */
const RESOURCE_KINDS = ["repo", "path", "confluence_space", "slack_channel"] as const;
const HIDE_KINDS = ["repo", "path"] as const;
type ResourceKind = (typeof RESOURCE_KINDS)[number];
type RuleList = "allow" | "deny" | "hide";

export interface AccessGroupEditorProps {
  readonly facets: GroupFacets;
  readonly units: readonly BusinessUnitOption[];
  readonly users: readonly AdminUser[];
  readonly ports: AccessWritePorts;
  readonly onClose: () => void;
  /** Called after any successful write so the page can re-read this group rather than guess at it. */
  readonly onChanged: () => void;
}

/**
 * Report one write.
 *
 * A write is three outcomes, not two: it changed something, it changed nothing because the row was already
 * there, or it changed something and the server has a caveat about it. Collapsing the middle case into
 * success is how an administrator comes to believe a duplicate grant was the one they meant to make.
 */
function useWriteReporter(onChanged: () => void): {
  readonly busy: boolean;
  readonly warnings: readonly PolicyWarning[];
  readonly run: (label: string, write: () => Promise<PolicyWriteResult | void>) => Promise<void>;
} {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState<readonly PolicyWarning[]>([]);

  const run = useCallback(
    async (label: string, write: () => Promise<PolicyWriteResult | void>) => {
      setBusy(true);
      setWarnings([]);
      try {
        const result = await write();
        // A revocation answers a bare `{changed}` with no warnings key at all — checked, not assumed.
        const caveats = result?.warnings ?? [];
        setWarnings(caveats);
        if (result && "changed" in result && result.changed === 0) {
          toast.show(`${label}: nothing to change`, {
            tone: "info",
            detail: "That row was already there. No second copy was made.",
          });
        } else if (caveats.length > 0) {
          const first = caveats[0];
          toast.show(`${label} — with a caveat`, {
            tone: "info",
            ...(first === undefined ? {} : { detail: first.message }),
          });
        } else {
          toast.show(label, { tone: "success" });
        }
        onChanged();
      } catch (cause: unknown) {
        const error =
          cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        toast.show(`${label} failed`, { tone: "danger", detail: error.userMessage });
      } finally {
        setBusy(false);
      }
    },
    [onChanged, toast],
  );

  return { busy, warnings, run };
}

export function AccessGroupEditor({
  facets,
  units,
  users,
  ports,
  onClose,
  onChanged,
}: AccessGroupEditorProps): JSX.Element {
  const group = facets.group;
  const { busy, warnings, run } = useWriteReporter(onChanged);

  const [pane, setPane] = useState<"confers" | "members">("confers");
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? "");

  const unitName = useCallback(
    (id: string): string => units.find((unit) => unit.id === id)?.displayName ?? id,
    [units],
  );

  const reachable = useMemo(() => new Set(facets.scopeAccess.map((row) => row.bu_id)), [facets]);
  const memberIds = useMemo(() => new Set(facets.members.map((row) => row.user_id)), [facets]);

  return (
    <Modal
      open
      title={group.name}
      description="What this group confers, and who is in it. Both are the same fact seen from two sides."
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      {warnings.length === 0 ? null : (
        <Banner tone="warning">
          {warnings.map((warning) => warning.message).join(" ")}
        </Banner>
      )}

      <div className="row" role="tablist" aria-label="Group detail">
        <Button
          variant={pane === "confers" ? "primary" : "ghost"}
          onClick={() => setPane("confers")}
          title="What this group confers"
        >
          What it confers
        </Button>
        <Button
          variant={pane === "members" ? "primary" : "ghost"}
          onClick={() => setPane("members")}
          title="Who is in this group"
        >
          Who is in it ({facets.members.length})
        </Button>
      </div>

      {pane === "members" ? (
        <MemberEditor
          facets={facets}
          users={users}
          memberIds={memberIds}
          busy={busy}
          onAdd={(userId, email) =>
            run(`Added ${email} to ${group.name}`, () => ports.addMember(group.id, userId))
          }
          onRemove={(userId, email) =>
            run(`Removed ${email} from ${group.name}`, () => ports.removeMember(group.id, userId))
          }
        />
      ) : (
        <>
          <Card
            title="Name and description"
            actions={
              <Button variant="secondary" onClick={() => setRenaming(true)} disabled={busy}>
                Edit
              </Button>
            }
          >
            <p className="field__hint">
              {group.description ?? "No description. A group nobody can explain is a group nobody dares delete."}
            </p>
          </Card>

          <ScopeAccessEditor
            facets={facets}
            units={units}
            unitName={unitName}
            busy={busy}
            onGrant={(buId) =>
              run(`${group.name} can now reach ${unitName(buId)}`, () => ports.grantScopeAccess(group.id, buId))
            }
            onRevoke={(buId) =>
              run(`${group.name} no longer reaches ${unitName(buId)}`, () =>
                ports.revokeScopeAccess(group.id, buId),
              )
            }
          />

          <ScopeRoleEditor
            facets={facets}
            units={units}
            unitName={unitName}
            reachable={reachable}
            busy={busy}
            onGrant={(buId, role) =>
              run(`${roleLabel(role)} granted in ${unitName(buId)}`, () =>
                ports.grantScopeRole(group.id, buId, role),
              )
            }
            onRevoke={(buId, role) =>
              run(`${roleLabel(role)} revoked in ${unitName(buId)}`, () =>
                ports.revokeScopeRole(group.id, buId, role),
              )
            }
          />

          <ResourceRuleEditor
            facets={facets}
            units={units}
            unitName={unitName}
            busy={busy}
            onAdd={(list, buId, kind, pattern) =>
              run(`${humanise(list)} rule added`, () => ports.addResourceRule(group.id, list, buId, kind, pattern))
            }
            onRemove={(list, buId, kind, pattern) =>
              run(`${humanise(list)} rule removed`, () =>
                ports.removeResourceRule(group.id, list, buId, kind, pattern),
              )
            }
          />

          <SectionEditor
            facets={facets}
            busy={busy}
            onSet={(section, view, act) =>
              run(`${humanise(section)} section updated`, () => ports.setSection(group.id, section, view, act))
            }
            onRemove={(section) =>
              run(`${humanise(section)} section removed`, () => ports.removeSection(group.id, section))
            }
          />
        </>
      )}

      <Modal
        open={renaming}
        title="Edit group"
        onClose={() => setRenaming(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy || name.trim() === ""}
              onClick={() => {
                void run("Group updated", () =>
                  ports.updateGroup(group.id, name.trim(), description.trim() === "" ? null : description.trim()),
                ).then(() => setRenaming(false));
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <TextField label="Name" value={name} onChange={setName} required />
        <TextField
          label="Description"
          value={description}
          onChange={setDescription}
          hint="What this group is for, in one line. Leave it blank to clear it."
        />
      </Modal>
    </Modal>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * Who is in it
 * ------------------------------------------------------------------------------------------------- */

interface MemberView {
  readonly userId: string;
  readonly email: string;
  readonly status: string;
  readonly addedAt: string;
}

function MemberEditor({
  facets,
  users,
  memberIds,
  busy,
  onAdd,
  onRemove,
}: {
  readonly facets: GroupFacets;
  readonly users: readonly AdminUser[];
  readonly memberIds: ReadonlySet<string>;
  readonly busy: boolean;
  readonly onAdd: (userId: string, email: string) => void;
  readonly onRemove: (userId: string, email: string) => void;
}): JSX.Element {
  const [candidate, setCandidate] = useState("");
  const [confirming, setConfirming] = useState<MemberView | null>(null);

  // The membership row carries a user id and nothing else, so the roster is joined here. A member whose
  // account has since been deleted still shows — as its id — rather than vanishing from a list that is
  // meant to account for everyone the group lets in.
  const byId = new Map(users.map((user) => [user.id, user]));
  const rows: readonly MemberView[] = facets.members.map((row) => {
    const user = byId.get(row.user_id);
    return {
      userId: row.user_id,
      email: user?.email ?? row.user_id,
      status: user?.status ?? "unknown",
      addedAt: row.added_at,
    };
  });

  const candidates = users
    .filter((user) => !memberIds.has(user.id))
    .map((user) => ({ value: user.id, label: `${user.email} (${humanise(user.status)})` }));

  const columns: readonly Column<MemberView>[] = [
    { key: "email", header: "Person", type: "text", value: (row) => row.email },
    {
      key: "status",
      header: "Account",
      type: "text",
      value: (row) => row.status,
      render: (row) => (
        <Badge tone={row.status === "active" ? "success" : row.status === "suspended" ? "danger" : "warning"}>
          {humanise(row.status)}
        </Badge>
      ),
    },
    {
      key: "addedAt",
      header: "Added",
      type: "date",
      value: (row) => row.addedAt,
      render: (row) => <time title={formatDateTime(row.addedAt)}>{formatDateTime(row.addedAt)}</time>,
      hideOnMobile: true,
    },
    {
      key: "actions",
      actions: true,
      header: "Actions",
      type: "text",
      sortable: false,
      value: () => "",
      render: (row) => (
        <Button variant="danger" size="sm" disabled={busy} onClick={() => setConfirming(row)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card title="Add someone">
        <div className="row">
          <Select
            label="Person"
            value={candidate}
            options={[{ value: "", label: "Choose a person…" }, ...candidates]}
            onChange={setCandidate}
            disabled={busy || candidates.length === 0}
            hint={
              candidates.length === 0
                ? "Everyone on the roster is already in this group."
                : "Membership is what carries this group's grants to a person. It confers nothing on its own."
            }
          />
          <Button
            variant="primary"
            disabled={busy || candidate === ""}
            onClick={() => {
              const chosen = users.find((user) => user.id === candidate);
              if (!chosen) return;
              onAdd(chosen.id, chosen.email);
              setCandidate("");
            }}
          >
            Add
          </Button>
        </div>
      </Card>

      <Table
        caption="Members of this group"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.userId}
        defaultSort={{ key: "email", direction: "ascending" }}
        titleKey="email"
        mobile="cards"
        empty={{
          title: "Nobody is in this group",
          body: "A group with no members grants nothing to anybody, however much it confers. Add someone above.",
        }}
      />

      <Modal
        open={confirming !== null}
        role="alertdialog"
        title="Remove from group"
        onClose={() => setConfirming(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => {
                if (confirming) onRemove(confirming.userId, confirming.email);
                setConfirming(null);
              }}
            >
              Remove
            </Button>
          </>
        }
      >
        <p>
          {confirming?.email} loses everything this group confers, from their next request. Removing a member
          is the only way to narrow scope access — access unions across a person&rsquo;s groups and has no
          deny — so check whether another group grants the same thing before assuming this takes it away.
        </p>
      </Modal>
    </>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * What it confers
 * ------------------------------------------------------------------------------------------------- */

function ScopeAccessEditor({
  facets,
  units,
  unitName,
  busy,
  onGrant,
  onRevoke,
}: {
  readonly facets: GroupFacets;
  readonly units: readonly BusinessUnitOption[];
  readonly unitName: (id: string) => string;
  readonly busy: boolean;
  readonly onGrant: (buId: string) => void;
  readonly onRevoke: (buId: string) => void;
}): JSX.Element {
  const [choice, setChoice] = useState("");
  const held = new Set(facets.scopeAccess.map((row) => row.bu_id));
  const options = units
    .filter((unit) => !held.has(unit.id))
    .map((unit) => ({
      value: unit.id,
      // A disabled unit is offerable and useless, so it is offered and labelled. Hiding it would leave an
      // administrator wondering why the unit they can see on the other screen is not on this list.
      label: unit.enabled ? unit.displayName : `${unit.displayName} — disabled, reaches nothing`,
    }));

  return (
    <Card title="Business units it can reach">
      {facets.scopeAccess.length === 0 ? (
        <p className="field__hint">
          None. Members of this group reach no business unit through it, whatever else it grants.
        </p>
      ) : (
        <div className="row">
          {facets.scopeAccess.map((row) => (
            <span key={row.bu_id} className="row">
              <Badge tone="success">{unitName(row.bu_id)}</Badge>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => onRevoke(row.bu_id)}>
                Revoke
              </Button>
            </span>
          ))}
        </div>
      )}

      <div className="row">
        <Select
          label="Grant access to"
          value={choice}
          options={[{ value: "", label: "Choose a business unit…" }, ...options]}
          onChange={setChoice}
          disabled={busy || options.length === 0}
        />
        <Button
          variant="primary"
          disabled={busy || choice === ""}
          onClick={() => {
            onGrant(choice);
            setChoice("");
          }}
        >
          Grant access
        </Button>
      </div>
    </Card>
  );
}

function ScopeRoleEditor({
  facets,
  units,
  unitName,
  reachable,
  busy,
  onGrant,
  onRevoke,
}: {
  readonly facets: GroupFacets;
  readonly units: readonly BusinessUnitOption[];
  readonly unitName: (id: string) => string;
  readonly reachable: ReadonlySet<string>;
  readonly busy: boolean;
  readonly onGrant: (buId: string, role: ScopeRole) => void;
  readonly onRevoke: (buId: string, role: ScopeRole) => void;
}): JSX.Element {
  const [bu, setBu] = useState("");
  const [role, setRole] = useState<ScopeRole>("job_author");

  return (
    <Card title="Roles within a business unit">
      {facets.scopeRoles.length === 0 ? (
        <p className="field__hint">None. Members can reach the units above but may not author updates in them.</p>
      ) : (
        <div className="row">
          {facets.scopeRoles.map((row) => {
            const dead = !reachable.has(row.bu_id);
            return (
              <span key={`${row.bu_id}:${row.role}`} className="row">
                <Badge tone={dead ? "warning" : "success"}>
                  {roleLabel(row.role)} in {unitName(row.bu_id)}
                  {dead ? " — no effect" : ""}
                </Badge>
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => onRevoke(row.bu_id, row.role)}>
                  Revoke
                </Button>
              </span>
            );
          })}
        </div>
      )}

      <div className="row">
        <Select
          label="Business unit"
          value={bu}
          options={[
            { value: "", label: "Choose a business unit…" },
            ...units.map((unit) => ({
              value: unit.id,
              // Said before the write, not after: the server accepts a role with no matching scope access
              // and returns a warning, and an administrator who reads it here does not make the mistake.
              label: reachable.has(unit.id)
                ? unit.displayName
                : `${unit.displayName} — no access granted yet, so a role here does nothing`,
            })),
          ]}
          onChange={setBu}
          disabled={busy}
        />
        <Select
          label="Role"
          value={role}
          options={SCOPE_ROLE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          onChange={(value) => setRole(value as ScopeRole)}
          disabled={busy}
        />
        <Button
          variant="primary"
          disabled={busy || bu === ""}
          onClick={() => {
            onGrant(bu, role);
            setBu("");
          }}
        >
          Grant role
        </Button>
      </div>
    </Card>
  );
}

function ResourceRuleEditor({
  facets,
  units,
  unitName,
  busy,
  onAdd,
  onRemove,
}: {
  readonly facets: GroupFacets;
  readonly units: readonly BusinessUnitOption[];
  readonly unitName: (id: string) => string;
  readonly busy: boolean;
  readonly onAdd: (list: RuleList, buId: string, kind: ResourceKind, pattern: string) => void;
  readonly onRemove: (list: RuleList, buId: string, kind: ResourceKind, pattern: string) => void;
}): JSX.Element {
  const [list, setList] = useState<RuleList>("allow");
  const [bu, setBu] = useState("");
  const [kind, setKind] = useState<ResourceKind>("repo");
  const [pattern, setPattern] = useState("");

  // The server answers `hide` on any other kind with a 404, which reads like a missing group. Narrowing the
  // control instead means the refusal cannot happen.
  const kinds: readonly ResourceKind[] = list === "hide" ? HIDE_KINDS : RESOURCE_KINDS;
  const kindValid = kinds.includes(kind);

  const rules: readonly { readonly list: RuleList; readonly bu_id: string; readonly kind: ResourceKind; readonly pattern: string }[] =
    [
      ...facets.resourceAllow.map((row) => ({ list: "allow" as const, ...row })),
      ...facets.resourceDeny.map((row) => ({ list: "deny" as const, ...row })),
      ...facets.resourceHide.map((row) => ({ list: "hide" as const, ...row })),
    ];

  return (
    <Card title="Resource rules">
      {rules.length === 0 ? (
        <p className="field__hint">
          No rules. The allow list is a positive gate: with nothing on it this group offers no repository,
          space or channel of its own — the business unit&rsquo;s own allowlists are what stand.
        </p>
      ) : (
        <div className="row">
          {rules.map((rule) => (
            <span key={`${rule.list}:${rule.bu_id}:${rule.kind}:${rule.pattern}`} className="row">
              <Badge tone={rule.list === "deny" ? "danger" : rule.list === "hide" ? "warning" : "success"}>
                {humanise(rule.list)} {resourceKindLabel(rule.kind)} {rule.pattern} in {unitName(rule.bu_id)}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onRemove(rule.list, rule.bu_id, rule.kind, rule.pattern)}
              >
                Remove
              </Button>
            </span>
          ))}
        </div>
      )}

      <div className="row">
        <Select
          label="List"
          value={list}
          options={[
            { value: "allow", label: "Allow — offer this, and only what is allowed" },
            { value: "deny", label: "Deny — refuse this, whatever else allows it" },
            { value: "hide", label: "Hide — keep it out of pickers (repositories and paths only)" },
          ]}
          onChange={(value) => {
            const next = value as RuleList;
            setList(next);
            if (next === "hide" && !HIDE_KINDS.includes(kind as (typeof HIDE_KINDS)[number])) setKind("repo");
          }}
          disabled={busy}
        />
        <Select
          label="Business unit"
          value={bu}
          options={[
            { value: "", label: "Choose a business unit…" },
            ...units.map((unit) => ({ value: unit.id, label: unit.displayName })),
          ]}
          onChange={setBu}
          disabled={busy}
        />
        <Select
          label="Kind"
          value={kind}
          options={kinds.map((value) => ({ value, label: resourceKindLabel(value) }))}
          onChange={(value) => setKind(value as ResourceKind)}
          disabled={busy}
        />
        <TextField
          label="Pattern"
          value={pattern}
          onChange={setPattern}
          placeholder="tropicalsun/c4s-ai/ticketless"
          hint="An exact value, or a prefix ending in a slash."
        />
        <Button
          variant="primary"
          disabled={busy || bu === "" || pattern.trim() === "" || !kindValid}
          onClick={() => {
            onAdd(list, bu, kind, pattern.trim());
            setPattern("");
          }}
        >
          Add rule
        </Button>
      </div>
    </Card>
  );
}

function SectionEditor({
  facets,
  busy,
  onSet,
  onRemove,
}: {
  readonly facets: GroupFacets;
  readonly busy: boolean;
  readonly onSet: (section: AdminSection, view: boolean, act: boolean) => void;
  readonly onRemove: (section: AdminSection) => void;
}): JSX.Element {
  const held = new Map(facets.adminSections.map((row) => [row.section, row]));

  return (
    <Card title="Administrative sections">
      <p className="field__hint">
        A section grant lets a group <em>read</em> an administrative screen. It does not let them change
        anything: every administrative write in this application is the superuser&rsquo;s alone, so
        &ldquo;view and change&rdquo; grants the reading and no more. That is a hard rail of the permission
        model, not an oversight.
      </p>
      {GRANTABLE_SECTIONS.map((section) => {
        const row = held.get(section);
        const view = row !== undefined;
        const act = row?.can_act === 1;
        return (
          <div key={section} className="row">
            <span>{humanise(section)}</span>
            <Checkbox
              label="Can view"
              checked={view}
              disabled={busy}
              onChange={(checked) => (checked ? onSet(section, true, act) : onRemove(section))}
            />
            <Checkbox
              label="Can change"
              checked={act}
              disabled={busy || !view}
              {...(view ? {} : { hint: "Grant viewing first — change is clamped down to view." })}
              onChange={(checked) => onSet(section, true, checked)}
            />
          </div>
        );
      })}
      {facets.adminSections.length === 0 ? (
        <EmptyState
          title="No administrative sections"
          body="Members of this group see the updates for the business units it reaches, and no administrative screen at all."
        />
      ) : null}
    </Card>
  );
}
